import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import type { Channel, PhoneSenderApi, PhoneSenderOption, TelegramSenderOption } from './types';

interface PhoneChannelResult {
  loading: boolean;
  senders: PhoneSenderOption[];
  defaultId: string | null;
}

interface TelegramChannelResult {
  loading: boolean;
  senders: TelegramSenderOption[];
  defaultId: string | null;
}

function mapPhoneSenders(raw: PhoneSenderApi[], channel: 'sms' | 'whatsapp'): PhoneSenderOption[] {
  const out: PhoneSenderOption[] = [];
  for (const s of raw) {
    const t = (s.type ?? s.channel_type) as 'sms' | 'whatsapp' | 'both' | 'telegram' | undefined;
    if (!t || t === 'telegram') continue;
    const matches = t === channel || t === 'both';
    if (!matches) continue;
    out.push({
      id: `${s.id}::${channel}`,
      phone_number: s.phone_number,
      label: s.label ?? s.display_name,
      isDefault: s.is_default,
    });
  }
  return out;
}

function mapTelegramSenders(raw: PhoneSenderApi[]): TelegramSenderOption[] {
  const out: TelegramSenderOption[] = [];
  for (const s of raw) {
    const t = (s.type ?? s.channel_type) as 'sms' | 'whatsapp' | 'both' | 'telegram' | undefined;
    if (t !== 'telegram') continue;
    if (s.status && s.status !== 'active') continue;
    const integrationId = s.metadata?.integration_id;
    if (!integrationId) continue;
    const username = s.metadata?.username ?? null;
    out.push({
      id: `${s.id}::tg`,
      integrationId,
      display: username ? `@${username}` : (s.display_name || s.phone_number),
      label: s.display_name ?? s.label,
    });
  }
  return out;
}

export function usePhoneChannelSenders(open: boolean, channel: 'sms' | 'whatsapp'): PhoneChannelResult {
  const [loading, setLoading] = useState(false);
  const [senders, setSenders] = useState<PhoneSenderOption[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<{ senders: PhoneSenderApi[] }>('/messaging/senders?include=all')
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data?.senders) {
          const mapped = mapPhoneSenders(res.data.senders, channel);
          setSenders(mapped);
          const def = mapped.find((s) => s.isDefault) ?? mapped[0];
          setDefaultId(def?.id ?? null);
        } else {
          setSenders([]);
          setDefaultId(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSenders([]);
          setDefaultId(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, channel]);

  return { loading, senders, defaultId };
}

export function useTelegramChannelSenders(open: boolean): TelegramChannelResult {
  const [loading, setLoading] = useState(false);
  const [senders, setSenders] = useState<TelegramSenderOption[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<{ senders: PhoneSenderApi[] }>('/messaging/senders?include=all')
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data?.senders) {
          const mapped = mapTelegramSenders(res.data.senders);
          setSenders(mapped);
          setDefaultId(mapped[0]?.id ?? null);
        } else {
          setSenders([]);
          setDefaultId(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSenders([]);
          setDefaultId(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  return { loading, senders, defaultId };
}

export type { Channel };
